import type { CSSProperties } from 'react';
import { cx } from '@parrot/utils';
import styles from './SplitBar.module.css';

export interface SplitBarSegment {
  /** Share of the filled part, 0–1. */
  fraction: number;
  /** A colour token, e.g. 'var(--metric-acquired)'. */
  color: string;
}

export interface SplitBarProps {
  /** One segment for a plain magnitude bar, several for a split (acquired / retained). */
  segments: SplitBarSegment[];
  /** How much of the track the fill spans, 0–1 — a row's size against the largest. */
  extent?: number;
  size?: 'sm' | 'md' | 'lg';
  /** Separate rounded segments with a hairline gap (many-part splits, e.g. by platform). */
  gapped?: boolean;
  className?: string;
  'aria-label'?: string;
}

const pct = (v: number) => `${Math.max(0, Math.min(1, v)) * 100}%`;

/**
 * A horizontal bar on a track: the fill spans `extent` of it, divided into
 * coloured segments. Every bar on the platform profile — magnitudes, shares,
 * acquired/retained splits — is one of these, so they share height, radius
 * and track. Widths and colours are data, so they go in as custom properties.
 */
export function SplitBar({ segments, extent = 1, size = 'sm', gapped = false, className, ...aria }: SplitBarProps) {
  return (
    <span
      className={cx(styles.track, styles[size], gapped && styles.gapped, className)}
      role={aria['aria-label'] ? 'img' : undefined}
      aria-label={aria['aria-label']}
      aria-hidden={aria['aria-label'] ? undefined : true}
    >
      <span className={styles.fill} style={{ '--extent': pct(extent) } as CSSProperties}>
        {segments.map((seg, i) => (
          <span
            key={i}
            className={styles.segment}
            style={{ '--share': pct(seg.fraction), '--grow': seg.fraction, '--color': seg.color } as CSSProperties}
          />
        ))}
      </span>
    </span>
  );
}
